import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CircleDollarSign,
  CreditCard,
  ExternalLink,
  RefreshCcw,
  Search,
  ShieldAlert,
  Wallet,
} from 'lucide-react';
import {
  adminApi,
  type AdminBillingDetailResponse,
  type AdminBillingOverviewResponse,
} from '@/api/client';
import { StatusBadge } from '@/components/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useErrorToast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { formatCompactNumber, formatMoney, formatTime, timeAgo } from '@/util/format';

const PAGE_SIZE = 50;
const PLAN_FILTERS = ['all', 'free', 'pro', 'enterprise'] as const;
const ACCESS_FILTERS = ['all', 'active', 'past_due', 'suspended'] as const;
const SUBSCRIPTION_FILTERS = [
  'all',
  'none',
  'active',
  'trialing',
  'past_due',
  'unpaid',
  'incomplete',
  'incomplete_expired',
  'paused',
  'canceled',
] as const;
const INVOICE_FILTERS = ['all', 'none', 'draft', 'open', 'paid', 'uncollectible', 'void'] as const;

type FilterValue<T extends readonly string[]> = T[number];

function MetricCard({
  title,
  value,
  description,
  icon: Icon,
}: {
  title: string;
  value: string;
  description: string;
  icon: typeof CreditCard;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardDescription>{title}</CardDescription>
          <CardTitle className="mt-2 text-2xl">{value}</CardTitle>
        </div>
        <div className="rounded-md border border-border/60 p-2 text-muted-foreground">
          <Icon className="size-4" />
        </div>
      </CardHeader>
      <CardContent className="pt-0 text-xs text-muted-foreground">{description}</CardContent>
    </Card>
  );
}

function StatusPill({ value, kind }: { value: string | null; kind: 'subscription' | 'invoice' }) {
  if (!value) {
    return (
      <Badge variant="outline" className="uppercase tracking-wide text-muted-foreground">
        none
      </Badge>
    );
  }

  const palette =
    kind === 'subscription'
      ? {
          active: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-500',
          trialing: 'border-sky-500/30 bg-sky-500/10 text-sky-500',
          past_due: 'border-amber-500/30 bg-amber-500/10 text-amber-500',
          unpaid: 'border-orange-500/30 bg-orange-500/10 text-orange-500',
          canceled: 'border-slate-500/30 bg-slate-500/10 text-slate-500',
          incomplete: 'border-violet-500/30 bg-violet-500/10 text-violet-500',
          incomplete_expired: 'border-red-500/30 bg-red-500/10 text-red-500',
          paused: 'border-zinc-500/30 bg-zinc-500/10 text-zinc-500',
        }
      : {
          draft: 'border-slate-500/30 bg-slate-500/10 text-slate-500',
          open: 'border-amber-500/30 bg-amber-500/10 text-amber-500',
          paid: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-500',
          uncollectible: 'border-red-500/30 bg-red-500/10 text-red-500',
          void: 'border-zinc-500/30 bg-zinc-500/10 text-zinc-500',
        };

  return (
    <Badge
      variant="outline"
      className={cn(
        'uppercase tracking-wide',
        palette[value as keyof typeof palette] ?? 'text-muted-foreground',
      )}
    >
      {value}
    </Badge>
  );
}

function AnomalyBadge({ value }: { value: string }) {
  return (
    <Badge variant="outline" className="border-red-500/20 bg-red-500/10 text-red-500">
      {value.replaceAll('_', ' ')}
    </Badge>
  );
}

function BillingFieldGrid({
  fields,
}: {
  fields: Array<{ label: string; value: React.ReactNode }>;
}) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
      {fields.map((field) => (
        <div key={field.label} className="grid grid-cols-[160px_1fr] gap-2">
          <dt className="text-muted-foreground">{field.label}</dt>
          <dd className="break-all">{field.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function StripeLink({
  baseUrl,
  path,
  label,
}: {
  baseUrl: string | null;
  path: string | null;
  label: string;
}) {
  if (!baseUrl || !path) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <a
      href={`${baseUrl}${path}`}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
    >
      {label}
      <ExternalLink className="size-3" />
    </a>
  );
}

export function BillingPage() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [plan, setPlan] = useState<FilterValue<typeof PLAN_FILTERS>>('all');
  const [accessStatus, setAccessStatus] = useState<FilterValue<typeof ACCESS_FILTERS>>('all');
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<FilterValue<typeof SUBSCRIPTION_FILTERS>>('all');
  const [invoiceStatus, setInvoiceStatus] = useState<FilterValue<typeof INVOICE_FILTERS>>('all');
  const [anomaliesOnly, setAnomaliesOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const [overview, setOverview] = useState<AdminBillingOverviewResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminBillingDetailResponse | null>(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [refreshBusy, setRefreshBusy] = useState(false);
  const onError = useErrorToast();

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    setOffset(0);
  }, [debouncedSearch, plan, accessStatus, subscriptionStatus, invoiceStatus, anomaliesOnly]);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    adminApi
      .getBillingOverview({
        search: debouncedSearch || undefined,
        plan: plan === 'all' ? undefined : plan,
        accessStatus: accessStatus === 'all' ? undefined : accessStatus,
        subscriptionStatus: subscriptionStatus === 'all' ? undefined : subscriptionStatus,
        invoiceStatus: invoiceStatus === 'all' ? undefined : invoiceStatus,
        anomaliesOnly,
        limit: PAGE_SIZE,
        offset,
      })
      .then((response) => {
        if (!cancelled) {
          setOverview(response);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          onError(error);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setBusy(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    accessStatus,
    anomaliesOnly,
    debouncedSearch,
    invoiceStatus,
    offset,
    onError,
    plan,
    subscriptionStatus,
  ]);

  useEffect(() => {
    if (!selectedOrgId) {
      setDetail(null);
      return;
    }

    let cancelled = false;
    setDetailBusy(true);
    adminApi
      .getBillingOrg(selectedOrgId)
      .then((response) => {
        if (!cancelled) {
          setDetail(response);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          onError(error);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDetailBusy(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedOrgId, onError]);

  const mrrLabel = useMemo(() => {
    const currencies = overview?.summary.mrrByCurrency ?? [];
    if (currencies.length === 0) {
      return '—';
    }
    return currencies.map((entry) => `${formatMoney(entry.amount, entry.currency)}/mo`).join(' · ');
  }, [overview?.summary.mrrByCurrency]);

  const selectedRow = useMemo(() => {
    return overview?.data.find((row) => row.organizationId === selectedOrgId) ?? null;
  }, [overview?.data, selectedOrgId]);

  async function refreshDetail() {
    if (!selectedOrgId) {
      return;
    }
    setRefreshBusy(true);
    try {
      const response = await adminApi.refreshBillingOrg(selectedOrgId);
      setDetail(response);
      if (overview) {
        const latest = await adminApi.getBillingOverview({
          search: debouncedSearch || undefined,
          plan: plan === 'all' ? undefined : plan,
          accessStatus: accessStatus === 'all' ? undefined : accessStatus,
          subscriptionStatus: subscriptionStatus === 'all' ? undefined : subscriptionStatus,
          invoiceStatus: invoiceStatus === 'all' ? undefined : invoiceStatus,
          anomaliesOnly,
          limit: PAGE_SIZE,
          offset,
        });
        setOverview(latest);
      }
    } catch (error) {
      onError(error);
    } finally {
      setRefreshBusy(false);
    }
  }

  return (
    <>
      <div>
        <h1 className="text-2xl font-semibold">Billing</h1>
        <p className="text-sm text-muted-foreground">
          Cross-tenant billing health, Stripe linkage, subscription lifecycle, and reconciliation
          flags.
        </p>
      </div>

      {!overview?.stripeConfigured && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardHeader className="flex flex-row items-start gap-3 space-y-0">
            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-2 text-amber-500">
              <AlertTriangle className="size-4" />
            </div>
            <div>
              <CardTitle>Stripe not configured</CardTitle>
              <CardDescription>
                Local org billing data is still visible, but live Stripe enrichment and manual
                refresh are unavailable in this environment.
              </CardDescription>
            </div>
          </CardHeader>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
        <MetricCard
          title="Tenants"
          value={formatCompactNumber(overview?.summary.totalOrgs ?? 0)}
          description="Organizations visible to backoffice staff"
          icon={Wallet}
        />
        <MetricCard
          title="Paid"
          value={formatCompactNumber(overview?.summary.paidOrgs ?? 0)}
          description="Local paid plans or linked Stripe subscriptions"
          icon={CreditCard}
        />
        <MetricCard
          title="Past due"
          value={formatCompactNumber(overview?.summary.pastDueOrgs ?? 0)}
          description="Writes blocked for billing delinquency"
          icon={AlertTriangle}
        />
        <MetricCard
          title="Suspended"
          value={formatCompactNumber(overview?.summary.suspendedOrgs ?? 0)}
          description="Manually or system-suspended tenants"
          icon={ShieldAlert}
        />
        <MetricCard
          title="Anomalies"
          value={formatCompactNumber(overview?.summary.anomalyCount ?? 0)}
          description="Rows with billing reconciliation flags"
          icon={AlertTriangle}
        />
        <MetricCard
          title="Estimated MRR"
          value={mrrLabel}
          description="Normalized monthly recurring amount by currency"
          icon={CircleDollarSign}
        />
      </div>

      <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-[minmax(0,1.8fr)_150px_150px_170px_170px_auto_auto]">
        <div className="relative">
          <Search className="absolute top-2.5 left-2.5 size-4 text-muted-foreground" />
          <Input
            placeholder="Search org, slug, customer, subscription, email"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="pl-8"
          />
        </div>
        <Select value={plan} onValueChange={(value) => setPlan(value as typeof plan)}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Plan" />
          </SelectTrigger>
          <SelectContent>
            {PLAN_FILTERS.map((value) => (
              <SelectItem key={value} value={value}>
                {value === 'all' ? 'All plans' : value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={accessStatus}
          onValueChange={(value) => setAccessStatus(value as typeof accessStatus)}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Access status" />
          </SelectTrigger>
          <SelectContent>
            {ACCESS_FILTERS.map((value) => (
              <SelectItem key={value} value={value}>
                {value === 'all' ? 'All access states' : value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={subscriptionStatus}
          onValueChange={(value) => setSubscriptionStatus(value as typeof subscriptionStatus)}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Subscription status" />
          </SelectTrigger>
          <SelectContent>
            {SUBSCRIPTION_FILTERS.map((value) => (
              <SelectItem key={value} value={value}>
                {value === 'all' ? 'All subscription states' : value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={invoiceStatus}
          onValueChange={(value) => setInvoiceStatus(value as typeof invoiceStatus)}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Invoice status" />
          </SelectTrigger>
          <SelectContent>
            {INVOICE_FILTERS.map((value) => (
              <SelectItem key={value} value={value}>
                {value === 'all' ? 'All invoice states' : value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant={anomaliesOnly ? 'default' : 'outline'}
          onClick={() => setAnomaliesOnly((value) => !value)}
        >
          Anomalies only
        </Button>
        <div className="flex items-center justify-end text-sm text-muted-foreground">
          {busy ? 'Loading…' : `${overview?.total ?? 0} matches`}
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tenant</TableHead>
              <TableHead>Local</TableHead>
              <TableHead>Stripe</TableHead>
              <TableHead>Subscription</TableHead>
              <TableHead>Invoice</TableHead>
              <TableHead>Sync</TableHead>
              <TableHead>Flags</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {busy &&
              !overview &&
              Array.from({ length: 8 }).map((_, index) => (
                <TableRow key={`billing-skeleton-${index}`}>
                  <TableCell colSpan={8}>
                    <Skeleton className="h-8 w-full" />
                  </TableCell>
                </TableRow>
              ))}

            {overview?.data.map((row) => (
              <TableRow key={row.organizationId}>
                <TableCell>
                  <div className="font-medium">{row.name}</div>
                  <div className="font-mono text-xs text-muted-foreground">{row.slug}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">
                    {row.organizationId}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="capitalize">{row.localPlan}</div>
                  <div className="mt-1">
                    <StatusBadge status={row.accessStatus} />
                  </div>
                </TableCell>
                <TableCell>
                  <div className="font-mono text-xs">{row.stripeCustomerId ?? '—'}</div>
                  <div className="text-xs text-muted-foreground">
                    {row.stripeCustomerEmail ?? 'No customer email'}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="mb-1">
                    <StatusPill value={row.stripeSubscriptionStatus} kind="subscription" />
                  </div>
                  <div className="text-sm">
                    {formatMoney(row.recurringAmount, row.recurringCurrency)}
                    {row.billingCycle ? ` / ${row.billingCycle}` : ''}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Ends {formatTime(row.currentPeriodEnd)}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="mb-1">
                    <StatusPill value={row.latestInvoiceStatus} kind="invoice" />
                  </div>
                  <div className="text-sm">
                    Due {formatMoney(row.latestInvoiceAmountDue, row.latestInvoiceCurrency)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {timeAgo(row.latestInvoiceCreatedAt)}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  <div>{timeAgo(row.lastSyncedAt)}</div>
                  <div className="text-xs">Webhook {timeAgo(row.lastWebhookAt)}</div>
                </TableCell>
                <TableCell>
                  <div className="flex max-w-[280px] flex-wrap gap-1">
                    {row.anomalyFlags.length > 0 ? (
                      row.anomalyFlags
                        .slice(0, 2)
                        .map((flag) => <AnomalyBadge key={flag} value={flag} />)
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">
                        clean
                      </Badge>
                    )}
                    {row.anomalyFlags.length > 2 && (
                      <Badge variant="outline">+{row.anomalyFlags.length - 2}</Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedOrgId(row.organizationId)}
                  >
                    Inspect
                  </Button>
                </TableCell>
              </TableRow>
            ))}

            {overview && overview.data.length === 0 && !busy && (
              <TableRow>
                <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                  No billing rows match the current filters.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={busy || offset === 0}
          onClick={() => setOffset((value) => Math.max(0, value - PAGE_SIZE))}
        >
          Prev
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={busy || (overview?.total ?? 0) <= offset + PAGE_SIZE}
          onClick={() => setOffset((value) => value + PAGE_SIZE)}
        >
          Next
        </Button>
      </div>

      <Sheet open={Boolean(selectedOrgId)} onOpenChange={(open) => !open && setSelectedOrgId(null)}>
        <SheetContent className="w-full gap-0 sm:max-w-2xl">
          <SheetHeader className="border-b">
            <div className="flex items-start justify-between gap-4 pr-8">
              <div>
                <SheetTitle>
                  {detail?.data.name ?? selectedRow?.name ?? 'Billing detail'}
                </SheetTitle>
                <SheetDescription>
                  {detail?.data.slug ?? selectedRow?.slug ?? 'Loading tenant billing detail'}
                </SheetDescription>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <StatusBadge
                    status={detail?.data.local.accessStatus ?? selectedRow?.accessStatus}
                  />
                  {(detail?.data.anomalyFlags ?? selectedRow?.anomalyFlags ?? []).map((flag) => (
                    <AnomalyBadge key={flag} value={flag} />
                  ))}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={refreshBusy || !detail?.stripeConfigured}
                onClick={() => void refreshDetail()}
              >
                <RefreshCcw className={cn('mr-2 size-4', refreshBusy && 'animate-spin')} />
                Refresh
              </Button>
            </div>
          </SheetHeader>

          <div className="flex-1 space-y-4 overflow-y-auto p-4">
            {detailBusy && !detail && (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, index) => (
                  <Skeleton key={`detail-skeleton-${index}`} className="h-28 w-full" />
                ))}
              </div>
            )}

            {detail && (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle>Local entitlement record</CardTitle>
                    <CardDescription>
                      Platform access state and the local org billing linkage used by hosted.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <BillingFieldGrid
                      fields={[
                        {
                          label: 'Org id',
                          value: <span className="font-mono">{detail.data.organizationId}</span>,
                        },
                        { label: 'Created', value: formatTime(detail.data.createdAt) },
                        { label: 'Plan', value: detail.data.local.plan },
                        {
                          label: 'Access status',
                          value: <StatusBadge status={detail.data.local.accessStatus} />,
                        },
                        { label: 'Raw status', value: detail.data.local.rawStatus ?? '—' },
                        {
                          label: 'Stripe customer',
                          value: (
                            <span className="font-mono">
                              {detail.data.local.stripeCustomerId ?? '—'}
                            </span>
                          ),
                        },
                        {
                          label: 'Stripe subscription',
                          value: (
                            <span className="font-mono">
                              {detail.data.local.stripeSubscriptionId ?? '—'}
                            </span>
                          ),
                        },
                        { label: 'Billing cycle', value: detail.data.local.billingCycle ?? '—' },
                        {
                          label: 'Current period end',
                          value: formatTime(detail.data.local.currentPeriodEnd),
                        },
                        {
                          label: 'Cancel at period end',
                          value: detail.data.local.cancelAtPeriodEnd ? 'yes' : 'no',
                        },
                        { label: 'Cancel at', value: formatTime(detail.data.local.cancelAt) },
                      ]}
                    />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Snapshot</CardTitle>
                    <CardDescription>
                      Cached Stripe summary used for cross-tenant overview and reconciliation.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <BillingFieldGrid
                      fields={[
                        {
                          label: 'Customer id',
                          value: (
                            <span className="font-mono">
                              {detail.data.snapshot.stripeCustomerId ?? '—'}
                            </span>
                          ),
                        },
                        {
                          label: 'Customer email',
                          value: detail.data.snapshot.stripeCustomerEmail ?? '—',
                        },
                        {
                          label: 'Subscription id',
                          value: (
                            <span className="font-mono">
                              {detail.data.snapshot.stripeSubscriptionId ?? '—'}
                            </span>
                          ),
                        },
                        {
                          label: 'Subscription status',
                          value: (
                            <StatusPill
                              value={detail.data.snapshot.stripeSubscriptionStatus}
                              kind="subscription"
                            />
                          ),
                        },
                        {
                          label: 'Recurring amount',
                          value: formatMoney(
                            detail.data.snapshot.recurringAmount,
                            detail.data.snapshot.recurringCurrency,
                          ),
                        },
                        { label: 'Billing cycle', value: detail.data.snapshot.billingCycle ?? '—' },
                        {
                          label: 'Period start',
                          value: formatTime(detail.data.snapshot.currentPeriodStart),
                        },
                        {
                          label: 'Period end',
                          value: formatTime(detail.data.snapshot.currentPeriodEnd),
                        },
                        {
                          label: 'Latest invoice id',
                          value: detail.data.snapshot.latestInvoiceId ?? '—',
                        },
                        {
                          label: 'Latest invoice status',
                          value: (
                            <StatusPill
                              value={detail.data.snapshot.latestInvoiceStatus}
                              kind="invoice"
                            />
                          ),
                        },
                        {
                          label: 'Latest invoice due',
                          value: formatMoney(
                            detail.data.snapshot.latestInvoiceAmountDue,
                            detail.data.snapshot.latestInvoiceCurrency,
                          ),
                        },
                        {
                          label: 'Default card',
                          value:
                            detail.data.snapshot.defaultPaymentMethodBrand &&
                            detail.data.snapshot.defaultPaymentMethodLast4
                              ? `${detail.data.snapshot.defaultPaymentMethodBrand} •••• ${detail.data.snapshot.defaultPaymentMethodLast4}`
                              : '—',
                        },
                        {
                          label: 'Last webhook',
                          value: formatTime(detail.data.snapshot.lastWebhookAt),
                        },
                        {
                          label: 'Last synced',
                          value: formatTime(detail.data.snapshot.lastSyncedAt),
                        },
                      ]}
                    />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Live Stripe detail</CardTitle>
                    <CardDescription>
                      Current customer, subscription, and invoice data fetched directly from Stripe.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {detail.data.live ? (
                      <>
                        <BillingFieldGrid
                          fields={[
                            {
                              label: 'Customer',
                              value: (
                                <div className="space-y-1">
                                  <div className="font-mono text-xs">
                                    {detail.data.live.customer.id}
                                  </div>
                                  <div>{detail.data.live.customer.email ?? '—'}</div>
                                  <StripeLink
                                    baseUrl={detail.stripeDashboardBaseUrl}
                                    path={`/customers/${detail.data.live.customer.id}`}
                                    label="Open in Stripe"
                                  />
                                </div>
                              ),
                            },
                            {
                              label: 'Metadata org',
                              value: detail.data.live.customer.metadataOrganizationId ?? '—',
                            },
                            {
                              label: 'Subscription',
                              value: detail.data.live.subscription ? (
                                <div className="space-y-1">
                                  <div className="font-mono text-xs">
                                    {detail.data.live.subscription.id}
                                  </div>
                                  <StatusPill
                                    value={detail.data.live.subscription.status}
                                    kind="subscription"
                                  />
                                  <StripeLink
                                    baseUrl={detail.stripeDashboardBaseUrl}
                                    path={`/subscriptions/${detail.data.live.subscription.id}`}
                                    label="Open in Stripe"
                                  />
                                </div>
                              ) : (
                                '—'
                              ),
                            },
                            {
                              label: 'Recurring',
                              value: detail.data.live.subscription
                                ? `${formatMoney(
                                    detail.data.live.subscription.recurringAmount,
                                    detail.data.live.subscription.recurringCurrency,
                                  )} / ${detail.data.live.subscription.billingCycle ?? 'unknown'}`
                                : '—',
                            },
                            {
                              label: 'Live period end',
                              value: formatTime(
                                detail.data.live.subscription?.currentPeriodEnd ?? null,
                              ),
                            },
                            {
                              label: 'Cancel at period end',
                              value: detail.data.live.subscription?.cancelAtPeriodEnd
                                ? 'yes'
                                : 'no',
                            },
                            {
                              label: 'Default payment method',
                              value: detail.data.live.defaultPaymentMethod
                                ? `${detail.data.live.defaultPaymentMethod.brand ?? 'card'} •••• ${detail.data.live.defaultPaymentMethod.last4 ?? '—'}`
                                : '—',
                            },
                          ]}
                        />

                        <div className="rounded-md border">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Invoice</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Due</TableHead>
                                <TableHead>Created</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {detail.data.live.invoices.slice(0, 5).map((invoice) => (
                                <TableRow key={invoice.id}>
                                  <TableCell>
                                    <div className="font-mono text-xs">{invoice.id}</div>
                                    <StripeLink
                                      baseUrl={detail.stripeDashboardBaseUrl}
                                      path={`/invoices/${invoice.id}`}
                                      label="Stripe"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <StatusPill value={invoice.status} kind="invoice" />
                                  </TableCell>
                                  <TableCell>
                                    {formatMoney(invoice.amountDue, invoice.currency)}
                                  </TableCell>
                                  <TableCell className="text-muted-foreground">
                                    {formatTime(invoice.createdAt)}
                                  </TableCell>
                                </TableRow>
                              ))}
                              {detail.data.live.invoices.length === 0 && (
                                <TableRow>
                                  <TableCell
                                    colSpan={4}
                                    className="h-16 text-center text-muted-foreground"
                                  >
                                    No invoices returned by Stripe.
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      </>
                    ) : (
                      <div className="text-sm text-muted-foreground">
                        No live Stripe detail is available for this org.
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Recent usage rollups</CardTitle>
                    <CardDescription>
                      Latest per-day hosted usage snapshots for this org.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="rounded-md border p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Day</TableHead>
                          <TableHead>Metric</TableHead>
                          <TableHead>Count</TableHead>
                          <TableHead>Recorded</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {detail.data.recentUsage.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell>{row.day}</TableCell>
                            <TableCell>{row.metric}</TableCell>
                            <TableCell>{formatCompactNumber(row.count)}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatTime(row.recordedAt)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {detail.data.recentUsage.length === 0 && (
                          <TableRow>
                            <TableCell
                              colSpan={4}
                              className="h-16 text-center text-muted-foreground"
                            >
                              No usage rollups yet.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Recent billing events</CardTitle>
                    <CardDescription>
                      Stripe webhook events stored locally for reconciliation.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="rounded-md border p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Type</TableHead>
                          <TableHead>Stripe event</TableHead>
                          <TableHead>Received</TableHead>
                          <TableHead>Processed</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {detail.data.recentBillingEvents.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell className="font-mono text-xs">{row.type}</TableCell>
                            <TableCell className="font-mono text-xs">{row.stripeEventId}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatTime(row.receivedAt)}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatTime(row.processedAt)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {detail.data.recentBillingEvents.length === 0 && (
                          <TableRow>
                            <TableCell
                              colSpan={4}
                              className="h-16 text-center text-muted-foreground"
                            >
                              No billing webhook events stored.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
